import { createApi } from "@reduxjs/toolkit/query/react";
import authBaseQuery from "../../../../utils/authBaseQuery";

const ExecutionCampaignApi = createApi({
  reducerPath: "executionCampaignApi",
  baseQuery: authBaseQuery,
  endpoints: (builder) => ({
    getAllExeCampaigns: builder.query({
      query: () => "exe_campaign",
      transformResponse: (response) => response.data,
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    getExeCampaignById: builder.query({
      query: (id) => `exe_campaign/${id}`,
    }),

    addExeCampaign: builder.mutation({
      query: (newExeCampaign) => ({
        url: "exe_campaign",
        method: "POST",
        body: newExeCampaign,
      }),
      onQueryStarted: async (newExeCampaign, { dispatch, queryFulfilled }) => {
        try {
          const { data: addedExeCampaign } = await queryFulfilled;

          dispatch(
            ExecutionCampaignApi.util.updateQueryData(
              "getAllExeCampaigns",
              undefined,
              (draft) => {
                draft.unshift(addedExeCampaign.data);
              }
            )
          );
        } catch (error) {
          console.error("Failed to add execution campaign:", error);
        }
      },
    }),

    editExeCampaign: builder.mutation({
      query: ({ id, ...updatedExeCampaign }) => ({
        url: `exe_campaign/${id}`,
        method: "PUT",
        body: updatedExeCampaign,
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: returnedExeCampaign } = await queryFulfilled;

          dispatch(
            ExecutionCampaignApi.util.updateQueryData(
              "getAllExeCampaigns",
              undefined,
              (draft) => {
                const exeCampaignIndex = draft.findIndex(
                  (exeCampaign) => exeCampaign.id === id
                );
                if (exeCampaignIndex !== -1) {
                  draft[exeCampaignIndex] = returnedExeCampaign.data;
                }
              }
            )
          );
        } catch (error) {
          console.error("Failed to edit execution campaign:", error);
        }
      },
    }),

    deleteExeCampaign: builder.mutation({
      query: (id) => ({
        url: `exe_campaign/${id}`,
        method: "DELETE",
      }),
    }),

    getExeCampaignsNameWiseData: builder.query({
      query: ({ page, limit, search } = {}) => {
        const queryParams = [];

        if (page) queryParams.push(`page=${page}`);
        if (limit) queryParams.push(`limit=${limit}`);
        if (search) queryParams.push(`search=${encodeURIComponent(search)}`);

        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return `exe_campaign_name_wise${queryString}`;
      },
      transformResponse: (response) => response.data,
    }),

    getNewExeCampaignsNameWiseData: builder.query({
      query: ({ userId, search, page, limit } = {}) => {
        const params = new URLSearchParams();

        if (userId) params.append("userId", userId);
        if (search) params.append("exe_campaign_name", search);
        if (page !== undefined && page !== null) params.append("page", page);
        if (limit !== undefined && limit !== null)
          params.append("limit", limit);

        const queryString = params.toString();
        return `new_exe_campaign_name_wise${queryString ? `?${queryString}` : ""
          }`;
      },
      transformResponse: (response) => response.data,
    }),

    getAllExeCampaignList: builder.query({
      query: (id) => `exe_campaign_wise_list/${id}`,
      transformResponse: (response) => response.data,
    }),

    getAdvancedPaymentsByVendor: builder.query({
      query: (vendorObjId) => {
        const params = new URLSearchParams({
          advanced_payment_status: "active",
          vendor_obj_id: vendorObjId,
        });

        return `purchase/advanced_payment?${params.toString()}`;
      },
      transformResponse: (response) => response.data,
    }),

    verifyAdvancePurchase: builder.mutation({
      query: (data) => ({
        url: "v1/operation/advance_purchase",
        method: "POST",
        body: data,
      }),
    }),
    verifyPurchasedAdvancePurchase: builder.mutation({
      query: (data) => ({
        url: "purchase/advanced_payment_settlement/" + data?.vendorId,
        method: "PUT",
        body: data,
      }),
    }),
    getPostDatesByCampaign: builder.query({
      query: ({ campaignId, vendorId }) => {
        if (campaignId)
          return `v1/operation/get_post_dates?campaignId=${campaignId}`;
        if (vendorId) return `v1/operation/get_post_dates?vendorId=${vendorId}`;
        return "";
      },
    }),
  }),
});

export const {
  useGetAllExeCampaignsQuery,
  useGetExeCampaignByIdQuery,
  useAddExeCampaignMutation,
  useEditExeCampaignMutation,
  useDeleteExeCampaignMutation,
  useGetExeCampaignsNameWiseDataQuery,
  useGetNewExeCampaignsNameWiseDataQuery,
  useGetAllExeCampaignListQuery,
  useGetAdvancedPaymentsByVendorQuery,
  useVerifyAdvancePurchaseMutation,
  useVerifyPurchasedAdvancePurchaseMutation,
  useGetPostDatesByCampaignQuery,
} = ExecutionCampaignApi;

export default ExecutionCampaignApi;
